import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { portalExpansionApi } from '../lib/portal-expansion-api';
import { useToast, getErrorMessage } from '../hooks/use-toast';
import { ToastContainer } from '../components/toast';

export default function BankingReviewPage() {
  const [page, setPage] = useState(1);
  const [selectedBanking, setSelectedBanking] = useState<any | null>(null);
  const queryClient = useQueryClient();
  const { toasts, showToast, removeToast } = useToast();

  const { data, isLoading, error } = useQuery({
    queryKey: ['pending-banking', page],
    queryFn: () => portalExpansionApi.getPendingBanking(page, 20),
    retry: 1,
  });

  const verifyMutation = useMutation({
    mutationFn: (payload: {
      bankingId: string;
      decision: 'verified' | 'rejected';
      accountHolder?: string;
      institutionNumber?: string;
      transitNumber?: string;
      accountNumber?: string;
      notes?: string;
    }) => {
      const { bankingId, ...review } = payload;
      return portalExpansionApi.verifyBanking(bankingId, review);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pending-banking'] });
      queryClient.invalidateQueries({ queryKey: ['onboarding-summary'] });
      setSelectedBanking(null);
      showToast('Banking record updated', 'success');
    },
    onError: (error: any) => {
      showToast(getErrorMessage(error), 'error');
    },
  });

  if (error) {
    return (
      <div className="error-state">
        <h2>Unable to load banking review queue</h2>
        <p>
          {(error as any)?.response?.status === 404
            ? 'This endpoint is not yet implemented in the backend.'
            : 'An error occurred loading the banking review queue.'}
        </p>
        <button onClick={() => window.location.reload()} className="btn">
          Retry
        </button>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="loading-state">
        <div className="spinner" />
        <p>Loading banking review queue...</p>
      </div>
    );
  }

  const records = data?.records || [];

  return (
    <>
      <ToastContainer toasts={toasts} removeToast={removeToast} />
      <div className="pagehead">
        <div>
          <h1>Banking Review</h1>
          <p>Review and verify banking documents with manual data transcription</p>
        </div>
      </div>

      {records.length === 0 ? (
        <div className="empty-state">
          <svg
            className="icon-large"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={1.65}
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
          </svg>
          <h3>All caught up</h3>
          <p>There are no banking documents pending review.</p>
        </div>
      ) : (
        <>
          <div className="table-container">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Member</th>
                  <th>Account Holder</th>
                  <th>Account Type</th>
                  <th>Version</th>
                  <th>Status</th>
                  <th>Document</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {records.map((record: any) => (
                  <tr key={record.id}>
                    <td>
                      <div className="member-cell">
                        <strong>{record.memberName}</strong>
                        <small>{record.memberEmail}</small>
                      </div>
                    </td>
                    <td>{record.accountHolder || '—'}</td>
                    <td>
                      {record.isOwnAccount ? 'Own account' : 'Third party'}
                      {record.accountExplanation && (
                        <small style={{ display: 'block', fontSize: '11px', color: '#68716d' }}>
                          {record.accountExplanation.slice(0, 50)}
                          {record.accountExplanation.length > 50 ? '...' : ''}
                        </small>
                      )}
                    </td>
                    <td>
                      <small style={{ fontSize: '11px', color: '#68716d' }}>
                        v{record.version}
                      </small>
                    </td>
                    <td>
                      <StatusBadge status={record.status} />
                    </td>
                    <td>
                      {record.documentId ? (
                        <span className="document-indicator">✓ Uploaded</span>
                      ) : (
                        '—'
                      )}
                    </td>
                    <td>
                      <button
                        onClick={() => setSelectedBanking(record)}
                        className="btn-small"
                      >
                        Review
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {data?.total > 20 && (
            <div className="pagination">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
                className="btn-secondary"
              >
                Previous
              </button>
              <span>
                Page {page} of {Math.ceil(data.total / 20)}
              </span>
              <button
                onClick={() => setPage((p) => p + 1)}
                disabled={page >= Math.ceil(data.total / 20)}
                className="btn-secondary"
              >
                Next
              </button>
            </div>
          )}
        </>
      )}

      {selectedBanking && (
        <BankingReviewModal
          record={selectedBanking}
          onClose={() => setSelectedBanking(null)}
          onSubmit={(data) =>
            verifyMutation.mutate({
              bankingId: selectedBanking.id,
              ...data,
            })
          }
          isSubmitting={verifyMutation.isPending}
        />
      )}

      <style>{`
        .pagehead {
          margin-bottom: 25px;
        }

        .pagehead h1 {
          font-size: 30px;
          font-weight: 600;
          letter-spacing: -0.85px;
          color: #313739;
        }

        .pagehead p {
          font-size: 13px;
          margin-top: 6px;
          color: #68716d;
        }

        .error-state,
        .loading-state,
        .empty-state {
          text-align: center;
          padding: 60px 20px;
        }

        .spinner {
          width: 40px;
          height: 40px;
          border: 4px solid #f2f3ee;
          border-top-color: #4b607f;
          border-radius: 50%;
          animation: spin 0.8s linear infinite;
          margin: 0 auto 20px;
        }

        @keyframes spin {
          to {
            transform: rotate(360deg);
          }
        }

        .error-state h2 {
          font-size: 18px;
          margin-bottom: 10px;
          color: #d14343;
        }

        .error-state p,
        .loading-state p {
          font-size: 13px;
          color: #68716d;
          margin-bottom: 20px;
        }

        .icon-large {
          width: 48px;
          height: 48px;
          color: #68716d;
          margin: 0 auto 16px;
        }

        .empty-state h3 {
          font-size: 16px;
          font-weight: 600;
          color: #68716d;
          margin-bottom: 8px;
        }

        .empty-state p {
          font-size: 13px;
          color: #68716d;
        }

        .table-container {
          background: white;
          border-radius: 12px;
          padding: 20px;
          margin-bottom: 20px;
          overflow-x: auto;
        }

        .data-table {
          width: 100%;
          border-collapse: collapse;
        }

        .data-table th {
          font-size: 11px;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          color: #68716d;
          text-align: left;
          padding: 12px 12px;
          border-bottom: 2px solid #f2f3ee;
        }

        .data-table td {
          padding: 16px 12px;
          border-bottom: 1px solid #f2f3ee;
          font-size: 13px;
          color: #424a47;
        }

        .data-table tbody tr:hover {
          background: #fafbf8;
        }

        .member-cell strong {
          display: block;
          font-size: 13px;
          font-weight: 500;
          color: #313739;
        }

        .member-cell small {
          display: block;
          font-size: 11px;
          color: #68716d;
          margin-top: 2px;
        }

        .account-ref {
          font-family: 'SF Mono', Consolas, monospace;
          font-size: 12px;
          background: #f9fafb;
          padding: 4px 8px;
          border-radius: 4px;
          color: #1f2937;
        }

        .document-indicator {
          font-size: 12px;
          color: #10b981;
        }

        .btn,
        .btn-small,
        .btn-secondary {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          padding: 8px 16px;
          border-radius: 8px;
          font-size: 13px;
          font-weight: 500;
          border: 1px solid #e6e9de;
          background: white;
          color: #424a47;
          cursor: pointer;
          transition: all 0.2s;
        }

        .btn:hover,
        .btn-small:hover,
        .btn-secondary:hover {
          background: #f6f6f3;
        }

        .btn-small {
          padding: 6px 12px;
          font-size: 12px;
        }

        .pagination {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 16px;
          padding: 20px;
        }

        .pagination button:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        @media (max-width: 768px) {
          .table-container {
            padding: 12px;
          }

          .data-table th,
          .data-table td {
            padding: 10px 8px;
            font-size: 12px;
          }
        }
      `}</style>
    </>
  );
}

function StatusBadge({ status }: { status: string }) {
  const colors: Record<string, string> = {
    verified: '#10b981',
    pending_extraction: '#f59e0b',
    pending_review: '#3b82f6',
    replaced: '#68716d',
    rejected: '#ef4444',
  };

  return (
    <span
      style={{
        display: 'inline-block',
        padding: '4px 8px',
        borderRadius: '6px',
        fontSize: '11px',
        fontWeight: 500,
        background: `${colors[status] || '#68716d'}15`,
        color: colors[status] || '#68716d',
      }}
    >
      {status.replace(/_/g, ' ')}
    </span>
  );
}

function BankingReviewModal({
  record,
  onClose,
  onSubmit,
  isSubmitting,
}: {
  record: any;
  onClose: () => void;
  onSubmit: (data: any) => void;
  isSubmitting: boolean;
}) {
  const [accountHolder, setAccountHolder] = useState(record.accountHolder || '');
  const [institutionNumber, setInstitutionNumber] = useState('');
  const [transitNumber, setTransitNumber] = useState('');
  const [accountNumber, setAccountNumber] = useState('');
  const [notes, setNotes] = useState('');
  const [documentUrl, setDocumentUrl] = useState<string | null>(null);

  const { data: _document } = useQuery({
    queryKey: ['document-preview', record.documentId],
    queryFn: async () => {
      if (!record.documentId) return null;
      const preview = await portalExpansionApi.getDocumentPreview(record.documentId);
      setDocumentUrl(preview.url);
      return preview;
    },
    enabled: !!record.documentId,
    retry: 1,
  });

  const handleVerify = () => {
    onSubmit({
      decision: 'verified',
      accountHolder: accountHolder.trim(),
      institutionNumber: institutionNumber.trim(),
      transitNumber: transitNumber.trim(),
      accountNumber: accountNumber.trim(),
      notes: notes.trim(),
    });
  };

  const handleReject = () => {
    onSubmit({
      decision: 'rejected',
      notes: notes.trim(),
    });
  };

  return (
    <>
      <div className="modal-overlay" onClick={onClose} />
      <div className="modal-large">
        <div className="modal-header">
          <h2>Review Banking Document</h2>
          <button onClick={onClose} className="modal-close" aria-label="Close">
            ×
          </button>
        </div>
        <div className="modal-body-split">
          <div className="modal-section">
            <h3>Document Preview</h3>
            {documentUrl ? (
              <div className="document-preview">
                <img src={documentUrl} alt="Banking document" />
              </div>
            ) : record.documentId ? (
              <div className="loading-preview">
                <div className="spinner-small" />
                <p>Loading document...</p>
              </div>
            ) : (
              <div className="no-document">
                <p>No document uploaded</p>
              </div>
            )}
          </div>

          <div className="modal-section">
            <h3>Manual Data Entry</h3>
            <p className="warning-text">
              ⚠️ Manual transcription - not automatic OCR. Verify all fields carefully.
            </p>

            <div className="form-group">
              <label htmlFor="bank-accountHolder">Account Holder Name *</label>
              <input
                type="text"
                id="bank-accountHolder" value={accountHolder}
                onChange={(e) => setAccountHolder(e.target.value)}
                placeholder="Full name as shown on document"
                disabled={isSubmitting}
              />
            </div>

            <div className="form-group">
              <label htmlFor="bank-institutionNumber">Institution Number (3 digits)</label>
              <input
                type="text"
                id="bank-institutionNumber" value={institutionNumber}
                onChange={(e) => setInstitutionNumber(e.target.value.replace(/\D/g, '').slice(0, 3))}
                placeholder="e.g., 001"
                maxLength={3}
                disabled={isSubmitting}
              />
            </div>

            <div className="form-group">
              <label htmlFor="bank-transitNumber">Transit Number (5 digits)</label>
              <input
                type="text"
                id="bank-transitNumber" value={transitNumber}
                onChange={(e) => setTransitNumber(e.target.value.replace(/\D/g, '').slice(0, 5))}
                placeholder="e.g., 12345"
                maxLength={5}
                disabled={isSubmitting}
              />
            </div>

            <div className="form-group">
              <label htmlFor="bank-accountNumber">Account Number (7-12 digits)</label>
              <input
                type="text"
                id="bank-accountNumber" value={accountNumber}
                onChange={(e) => setAccountNumber(e.target.value.replace(/\D/g, '').slice(0, 12))}
                placeholder="e.g., 1234567"
                maxLength={12}
                disabled={isSubmitting}
              />
              <small>Will be masked in member-facing views</small>
            </div>

            <div className="form-group">
              <label htmlFor="bank-adminNotes">Admin Notes</label>
              <textarea
                id="bank-adminNotes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Internal notes (not visible to member)"
                rows={3}
                disabled={isSubmitting}
              />
            </div>
          </div>
        </div>

        <div className="modal-footer">
          <button onClick={onClose} className="btn-secondary" disabled={isSubmitting}>
            Cancel
          </button>
          <button onClick={handleReject} className="btn-danger" disabled={isSubmitting}>
            Reject
          </button>
          <button
            onClick={handleVerify}
            className="btn-primary"
            disabled={isSubmitting || !accountHolder.trim()}
          >
            {isSubmitting ? 'Verifying...' : 'Verify & Save'}
          </button>
        </div>
      </div>

      <style>{`
        .modal-overlay {
          position: fixed;
          inset: 0;
          background: rgba(0, 0, 0, 0.5);
          z-index: 100;
        }

        .modal-large {
          position: fixed;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          background: white;
          border-radius: 12px;
          width: 95%;
          max-width: 1200px;
          max-height: 90vh;
          display: flex;
          flex-direction: column;
          z-index: 101;
        }

        .modal-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 20px 24px;
          border-bottom: 1px solid #f2f3ee;
        }

        .modal-header h2 {
          font-size: 18px;
          font-weight: 600;
          color: #313739;
        }

        .modal-close {
          font-size: 28px;
          line-height: 1;
          color: #68716d;
          background: none;
          border: 0;
          padding: 0;
          width: 32px;
          height: 32px;
          cursor: pointer;
        }

        .modal-body-split {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 24px;
          padding: 24px;
          overflow-y: auto;
          flex: 1;
        }

        .modal-section h3 {
          font-size: 14px;
          font-weight: 600;
          color: #313739;
          margin-bottom: 12px;
        }

        .document-preview {
          border: 1px solid #e5e7eb;
          border-radius: 8px;
          padding: 12px;
          background: #f9fafb;
          min-height: 400px;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .document-preview img {
          max-width: 100%;
          max-height: 600px;
          border-radius: 4px;
        }

        .loading-preview,
        .no-document {
          text-align: center;
          padding: 60px 20px;
          color: #68716d;
        }

        .spinner-small {
          width: 32px;
          height: 32px;
          border: 3px solid #f2f3ee;
          border-top-color: #4b607f;
          border-radius: 50%;
          animation: spin 0.8s linear infinite;
          margin: 0 auto 12px;
        }

        .warning-text {
          background: #fef3c7;
          border: 1px solid #fbbf24;
          color: #92400e;
          padding: 10px 12px;
          border-radius: 8px;
          font-size: 12px;
          margin-bottom: 16px;
        }

        .form-group {
          margin-bottom: 16px;
        }

        .form-group label {
          display: block;
          font-size: 12px;
          font-weight: 500;
          color: #374151;
          margin-bottom: 6px;
        }

        .form-group input,
        .form-group textarea {
          width: 100%;
          padding: 10px 12px;
          border: 1px solid #d1d5db;
          border-radius: 8px;
          font-size: 13px;
          color: #1f2937;
        }

        .form-group input:focus,
        .form-group textarea:focus {
          outline: none;
          border-color: #4b607f;
        }

        .form-group input:disabled,
        .form-group textarea:disabled {
          background: #f3f4f6;
          cursor: not-allowed;
        }

        .form-group small {
          display: block;
          font-size: 11px;
          color: #6b7280;
          margin-top: 4px;
        }

        .modal-footer {
          display: flex;
          gap: 12px;
          padding: 20px 24px;
          border-top: 1px solid #f2f3ee;
          justify-content: flex-end;
        }

        .btn-primary {
          padding: 10px 20px;
          border-radius: 8px;
          font-size: 13px;
          font-weight: 500;
          border: 0;
          background: #4b607f;
          color: white;
          cursor: pointer;
        }

        .btn-primary:hover:not(:disabled) {
          background: #3d4f68;
        }

        .btn-primary:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .btn-danger {
          padding: 10px 20px;
          border-radius: 8px;
          font-size: 13px;
          font-weight: 500;
          border: 0;
          background: #ef4444;
          color: white;
          cursor: pointer;
        }

        .btn-danger:hover:not(:disabled) {
          background: #dc2626;
        }

        .btn-danger:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        @media (max-width: 968px) {
          .modal-body-split {
            grid-template-columns: 1fr;
          }
        }

        @media (max-width: 640px) {
          .modal-large {
            width: 100%;
            height: 100%;
            max-width: none;
            max-height: none;
            border-radius: 0;
          }

          .modal-header,
          .modal-body-split,
          .modal-footer {
            padding: 16px;
          }
        }
      `}</style>
    </>
  );
}
